import './ImageGeneration.css';
import { Button, Divider, Form, message, Spin, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import PromptGenerator from '../../../components/PromptGenerator/PromptGenerator';
import PromptChoices from '../../../components/PromptChoices/PromptChoices';
import ImagePreview from './ImagePreview/ImagePreview';
import { useSelector } from 'react-redux';
import { selectIdentityToken, selectSessionId, selectSessionToken } from '../../../store/sessionSlice';
import { fetchRequest } from '../../../utils/Fetcher';
import { SaveProgress } from '../../../utils/SaveProgress';
import { runImageGenerationJob } from '../../../utils/polls/RunImageGenerationJob';
import { runPool } from '../../../utils/polls/RunPool';

const ImageGeneration = ({ onNext, selectedConceptIds, generatedImages, setGeneratedImages }) => {
  const desc = [
    "Upload & configure image generation",
    "Choose your preferred image description",
    "Generated image"
  ];

  const sessionId = useSelector(selectSessionId);
  const sessionToken = useSelector(selectSessionToken);
  const accessToken = useSelector(selectIdentityToken);

  const [messageApi, contextHolder] = message.useMessage();    

  const [flowIndex, setFlowIndex] = useState(0);

  const [fileList, setFileList] = useState([]);
  const [prompt, setPrompt] = useState([]);            
  const [selectedPrompt, setSelectedPrompt] = useState([]);

  const [aspectRatio, setAspectRatio] = useState("");
  const [applyBrandGuidelines, setApplyBrandGuidelines] = useState(true);
  const [gcsUri, setGcsUri] = useState([]);             
  
  const [loading, setLoading] = useState(false);
  const [loadingDesc, setLoadingDesc] = useState("");

  const [revisingKey, setRevisingKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);

  const [form] = Form.useForm();

  const isImageGenerating =
    flowIndex === 2 && (generatedImages ?? []).some((img) => img?.generating);

  const disableStep2Nav = loading || isImageGenerating;
  const disableBack = loading || isImageGenerating;

  const goBack = () => {
    if (disableBack) return;
    if (flowIndex === 2) {
      setGeneratedImages([]);
      setFlowIndex(1);
    } else if (flowIndex === 1) {
      setSelectedPrompt([]);
      setFlowIndex(0);
    }
  };

  const goNext = async () => {
    if (disableStep2Nav) return;

    try {
      if (flowIndex === 0) {
        if (generatedImages.length > 0 && prompt.length > 0) {
          setFlowIndex(2);
          return;
        }

        const values = await form.validateFields();

        const descValue = (values.description ?? "").trim();
        if (!descValue) {
          messageApi.warning("Please input the description.");
          return;
        }

        setLoading(true);

        let refImages;

        if (fileList.length > 0) {
          const uploaded = await handleImageUpload();
          refImages = uploaded.map((x) => ({ uri: x.gcsUri }));
          setGcsUri(uploaded);
        }

        const prompts = await generatePrompt(refImages, values);
        if (!prompts) return;

        setPrompt(prompts ?? []);
        setSelectedPrompt([]);
        setGeneratedImages([]);

        setFlowIndex(1);
        return;
      }

      if (flowIndex === 1) {
        if (!selectedPrompt?.length) {
          messageApi.warning("Please select at least one image description.");
          return;
        }

        setLoading(true);
        setLoadingDesc("AI is generating campaign images..");

        const refImages = getReferenceImages();

        await SaveProgress({
          sessionId: sessionId,
          method: "POST",
          param: `campaign-image/prompts/save`,
          payload: {
            selectedIds: selectedPrompt
          }
        });

        setLoading(false);
        setLoadingDesc("");

        const placeholders = selectedPrompt.map((p) => {
          const match = prompt.find((x) => String(x.id) === String(p));

          return {
            promptId: p,
            prompt: match?.description || "",
            generating: true,
          };
        });

        setGeneratedImages(placeholders);
        setFlowIndex(2);

        await runPool(
          selectedPrompt,
          async (p, index) => {
            try {
              const imgs = await generateImage({ refImages, chosenPrompt: p });

              const normalized = (imgs ?? []).map((it) => ({
                ...it,
                promptId: p,
                generating: false,
              }));

              setGeneratedImages((prev) => {
                const next = [...prev];

                if (normalized[0]) {
                  next[index] = normalized[0];
                }

                return next;
              });
            } catch (e) {
              console.error("Image generation failed:", e);
              setGeneratedImages((prev) => {
              const next = [...prev];
              next[index] = {
                ...next[index],
                generating: false,
                error: e?.message ?? "Generation failed",
              };
              return next;
            });
            }
          },
          3
        );
        return;
      }

      const imgId = (generatedImages ?? []).map((img) => img.id);
      await SaveProgress({
        sessionId: sessionId,
        method: "POST",
        param: `campaign-image/save`,
        payload: {
          selectedIds: imgId
        }
      });

      onNext?.();
    } catch (e) {
      messageApi.error(e?.message ?? "Something went wrong");
    } finally {
      setLoading(false);
      setLoadingDesc("");
    }
  };

  const handleImageUpload = async () => {
    setLoadingDesc("Uploading images..");
    const formData = new FormData();
    
    fileList.forEach((file) => {
      formData.append("files", file.originFileObj, file.name);
    });

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/sessions/uploads/images`,
      {
        method: "POST",
        headers: { "X-Session-Token": sessionToken, "Authorization": `Bearer ${accessToken}` },
        body: formData,
      }
    ).finally(() => {
      setLoadingDesc("");
    });

    const data = await res.json();

    if (!res.ok) {
      messageApi.error(`Error uploading images: ${data?.message ?? "Unknown error"}`);
      throw new Error(data?.message ?? "Upload failed");
    }

    const uploaded = (data?.data.uploaded ?? data?.uploaded ?? [])
      .filter((x) => x?.gcs_uri && x?.filename)
      .map((x) => ({
        gcsUri: x.gcs_uri,
        filename: x.filename,
      }));

    messageApi.success("Images uploaded successfully!");
    return uploaded;
  };

  const generatePrompt = async (refImages = [], values) => {
    setLoadingDesc("AI is generating your image descriptions..");

    setAspectRatio(values.aspect_ratio);
    setApplyBrandGuidelines(!!values.apply_brand_guidelines);

    const body = {
      campaignConceptId: selectedConceptIds,
      prompt: values.description,
      aspectRatio: values.aspect_ratio,
      numOfCampaignImage: values.num_images,
      ...(refImages ? { referenceImages: refImages } : {}),
    };

    const { result } = await fetchRequest({
      path: `sessions/campaign-image/prompts/generate`,
      method: "POST",
      data: JSON.stringify(body),
    }).finally(() => {
      setLoadingDesc("");
    });

    return result;
  };

  const getReferenceImages = () => (gcsUri ?? []).map((x) => ({ uri: x.gcsUri }));

  const buildImageGenerateBody = ({ promptText, refImages = getReferenceImages() }) => ({
    campaignConceptId: selectedConceptIds,
    prompt: promptText,
    aspectRatio,
    numOfCampaignImage: 1,
    referenceImages: refImages,
    applyBrandGuidelines,
  });

  const updateGeneratedImageAt = (idx, updater) => {
    setGeneratedImages((prev) => {
      const next = [...(prev ?? [])];
      const current = next[idx] ?? {};
      next[idx] = typeof updater === "function" ? updater(current) : updater;
      return next;
    });
  };

  const generateImage = async ({ refImages, chosenPrompt }) => {
    const match = (prompt ?? []).find(
      (p) => String(p?.id) === String(chosenPrompt)
    );

    const detailed = (match?.description ?? "").trim();
    const body = buildImageGenerateBody({
      promptText: detailed,
      refImages,
    });

    return runImageGenerationJob({ body });
  };

  const normalizeGeneratedImages = (payload) => {
    const list =
      payload?.data ??
      payload?.items ??
      payload?.generated_images ??
      payload?.images ??
      payload;

    if (!Array.isArray(list)) return [];

    return list.map((it, idx) => ({
      ...it,
      filename:
        it.filename ||
        it.file_name ||
        it.name ||
        `generated_${idx + 1}.jpg`,
    }));
  };

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;

    const load = async () => {
      try {
        if ((generatedImages ?? []).length > 0) {
          setFlowIndex(2);
          return;
        }

        setFlowIndex(0);
      } catch (e) {
        console.error(e);
        setFlowIndex(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, generatedImages?.length]);

  const findPromptById = (promptId) => (prompt ?? []).find((p) => String(p?.id) === String(promptId));

  const updatePromptById = (promptId, updater) => {
    setPrompt((prev) =>
      (prev ?? []).map((p) =>
        String(p?.id) === String(promptId)
          ? (typeof updater === "function" ? updater(p) : { ...p, ...updater })
          : p
      )
    );
  };

  const revisePromptById = async (promptId, instruction) => {
    const target = findPromptById(promptId);
    if (!target) throw new Error("Prompt not found");

    const { result } = await fetchRequest({
      path: `sessions/campaign-image/prompts/${target.id}/revise`,
      method: "PATCH",
      data: JSON.stringify({
        revisionInstructions: instruction,
      }),
    });

    const newDesc =
      result?.description ??
      result ??
      "";

    updatePromptById(promptId, { description: newDesc });

    return newDesc;
  };

  const handleRevisePrompt = async (key, instruction) => {
    try {
      setRevisingKey(key);
      await revisePromptById(key, instruction);
      return true;
    } catch (e) {
      messageApi.error(e?.message ?? "Failed to revise prompt");
      return false;
    } finally {
      setRevisingKey(null);
    }
  };

  const revisePromptDescription = async (promptId, instruction) => {
    return revisePromptById(promptId, instruction);
  };

  const regenerateImageAtIndex = async ({ indexKey, promptText }) => {
    const idx = Number(indexKey);

    try {
      updateGeneratedImageAt(idx, (current) => ({
        ...current,
        generating: true,
      }));

      const body = buildImageGenerateBody({ promptText });
      const images = await runImageGenerationJob({ body });
      const [newImg] = normalizeGeneratedImages(images) ?? [];

      if (!newImg) return false;

      updateGeneratedImageAt(idx, (current) => ({
        ...newImg,
        promptId: current?.promptId,
        prompt: promptText,
        generating: false,
      }));

      return true;
    } catch (e) {
      updateGeneratedImageAt(idx, (current) => ({
        ...current,
        generating: false,
      }));

      throw e;
    }
  };

  const handleEditPrompt = async (key, { title, description }) => {
    try {
      setEditingKey(key);

      const target = findPromptById(key);
      if (!target) return false;

      const { result } = await fetchRequest({
        path: `sessions/campaign-image/prompts/${target.id}/edit`,
        method: "PATCH",
        data: JSON.stringify({
          title,
          prompt: description,
        }),
      });

      const nextTitle = result?.title ?? title;
      const nextDesc =
        result?.description ??
        result?.detailed_prompt ??
        result?.detailedPrompt ??
        description;

      updatePromptById(key, {
        title: nextTitle,
        description: nextDesc,
      });

      messageApi.success("Prompt updated");
      return true;
    } catch (e) {
      messageApi.error(e?.message ?? "Failed to update prompt");
      return false;
    } finally {
      setEditingKey(null);
    }
  };

  return (
    <div className='image-generation-container'>
      {contextHolder}
      <div className="content-title-container">
        <div className="content-title">
          {desc[flowIndex]}
          <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_IMAGE_GENERATION}>
              <QuestionCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#888', cursor: 'pointer' }} />
          </Tooltip>
        </div>
        <Divider className="content-title-divider" />
      </div>

      {loading ? (
        <div className="loading-container">
          {loadingDesc}
          <Spin />
        </div>
      ) :
      (
        <div className="image-generation-content">
          {flowIndex === 0 && (
            <Form
              form={form}
              initialValues={{
                description: "",
                num_images: 2,
                aspect_ratio: "16:9",
                apply_brand_guidelines: true,
              }}
              onValuesChange={(changed) => {
                if (changed.description || changed.aspect_ratio || changed.num_images || changed.apply_brand_guidelines) {
                  setPrompt([]);
                  setSelectedPrompt([]);
                  setGeneratedImages([]);
                }
              }}
            >
              <PromptGenerator
                fileList={fileList}
                setFileList={(next) => {
                  setFileList(next);
                  setPrompt([]);
                  setSelectedPrompt([]);
                  setGeneratedImages([]);
                  setGcsUri([]);
                }}
              />
            </Form>
          )}

          {flowIndex === 1 && (
            <PromptChoices
              items={prompt}
              revisingKey={revisingKey}
              editingKey={editingKey}
              onChangeSelected={(selectedTexts) => {
                setSelectedPrompt(selectedTexts);
                setGeneratedImages([]);
              }}
              onRevisePrompt={handleRevisePrompt}
              onEditPrompt={handleEditPrompt}
            />
          )}

          {flowIndex === 2 && (
            <ImagePreview
              setGeneratedImages={setGeneratedImages}
              generatedImages={generatedImages}
              sessionId={sessionId}
              onReviseDescription={revisePromptDescription}
              onRegenerate={regenerateImageAtIndex}
            />
          )}
        </div>
      )}

      <div className="back-next-container">
        <div className="back-slot">
          {flowIndex !== 0 && (
            <Button
              onClick={goBack}
              className="next-button"
              disabled={disableBack}
            >
              Back
            </Button>
          )}
        </div>

        <div className='next-footer-container'>
          {flowIndex === 1 && selectedPrompt?.length > 0 && `${selectedPrompt.length} image${selectedPrompt.length > 1 ? 's' : ''} will be generated`}
          <Button
            onClick={goNext}
            className="next-button"
            disabled={disableStep2Nav}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageGeneration;